/**
 * Prompt Library Hooks
 *
 * Properly memoized hooks for accessing combined prompt data.
 * These hooks use useMemo to prevent infinite loops from recreating arrays.
 */

import { useMemo } from 'react';
import type { PromptTemplate, PromptCategory } from '@ctrl-enter/types';
import { SYSTEM_PROMPT_TEMPLATES } from '@ctrl-enter/types';
import {
  usePromptLibraryStore,
  useUserPrompts,
  useOrgPrompts,
} from '../stores/prompt-library-store';

/**
 * Get all prompts (system + org + user) - properly memoized
 */
export function useAllPrompts(): PromptTemplate[] {
  const userPrompts = useUserPrompts();
  const orgPrompts = useOrgPrompts();

  return useMemo(
    () => [...SYSTEM_PROMPT_TEMPLATES, ...orgPrompts, ...userPrompts],
    [orgPrompts, userPrompts]
  );
}

/**
 * Get prompts by category - properly memoized
 */
export function usePromptsByCategory(category: PromptCategory): PromptTemplate[] {
  const allPrompts = useAllPrompts();

  return useMemo(() => allPrompts.filter(p => p.category === category), [allPrompts, category]);
}

/**
 * Get favorite prompts - properly memoized
 */
export function useFavoritePrompts(): PromptTemplate[] {
  const allPrompts = useAllPrompts();
  const favoriteIds = usePromptLibraryStore(state => state.favoriteIds);

  return useMemo(() => allPrompts.filter(p => favoriteIds.has(p.id)), [allPrompts, favoriteIds]);
}

/**
 * Get recently used prompts - properly memoized
 */
export function useRecentlyUsedPrompts(): PromptTemplate[] {
  const allPrompts = useAllPrompts();
  const recentlyUsedIds = usePromptLibraryStore(state => state.recentlyUsedIds);

  return useMemo(() => {
    const promptMap = new Map(allPrompts.map(p => [p.id, p]));
    return recentlyUsedIds
      .map(id => promptMap.get(id))
      .filter((p): p is PromptTemplate => p !== undefined);
  }, [allPrompts, recentlyUsedIds]);
}

/**
 * Get prompt by ID - properly memoized
 */
export function usePromptById(id: string): PromptTemplate | undefined {
  const allPrompts = useAllPrompts();

  return useMemo(() => allPrompts.find(p => p.id === id), [allPrompts, id]);
}
